import * as THREE from 'three';
import { getDokumMalzemeDB, IDokumAlasim, DokumAlasimKategori, sicaklikLookup } from '../FikoDokumMalzemeDB';

// ============================================================================
// INTERFACES
// ============================================================================

export interface IGatingSonuc {
  besleyiciler: IBesleyici[];
  sprue: ISprue;
  runnerlar: IRunner[];
  gatelar: IGate[];
  verimYuzdesi: number;
  tahminiDolumSuresi: number;
  puan: number;
  parcaHacimCm3: number;
  parcaYuzeyAlaniCm2: number;
  parcaModulus: number;
  parcaAgirlikKg: number;
  brutAgirlikKg: number;
}

export interface IBesleyici {
  pozisyon: THREE.Vector3;
  cap: number;
  yukseklik: number;
  modulus: number;
  hotSpotId: string;
}

export interface ISprue {
  pozisyon: THREE.Vector3;
  ustCap: number;
  altCap: number;
  yukseklik: number;
}

export interface IRunner {
  baslangic: THREE.Vector3;
  bitis: THREE.Vector3;
  kesitAlani: number;
}

export interface IGate {
  pozisyon: THREE.Vector3;
  genislik: number;
  kalinlik: number;
  hiz: number;
}

export type DokumTuru = 'kum' | 'basincli' | 'hassas' | 'kabuk' | 'savurma';

interface IHotSpot {
  id: string;
  pozisyon: THREE.Vector3;
  modulus: number;
  hacimCm3: number;
}

// ============================================================================
// MALZEME BAZLI SABITLER
// ============================================================================

const MAX_GATE_HIZ: Record<string, number> = {
  aluminyum: 0.5,
  magnezyum: 0.5,
  cinko: 0.4,
  bakir: 0.8,
  demir_dokum: 1.5,
  celik_dokum: 2.0,
};

const DOKUM_VERIM: Record<DokumTuru, number> = {
  kum: 0.60,
  basincli: 0.85,
  hassas: 0.78,
  kabuk: 0.68,
  savurma: 0.72,
};

const BESLEYICI_MODULUS_KATSAYI = 1.3;
const BESLEYICI_CAP_KATSAYI = 6.0;
const BESLEYICI_YUKSEKLIK_ORANI = 1.2;
const SPRUE_KONIKLIK = 1.3;

// ============================================================================
// GATING TASARIM MOTORU
// ============================================================================

export class FikoGatingTasarim {
  private malzemeDB = getDokumMalzemeDB();
  private _tmpVec = new THREE.Vector3();
  private _tmpBox = new THREE.Box3();

  hesapla(
    mesh: THREE.Mesh,
    malzemeId: string,
    dokumTuru: DokumTuru,
    dokumSicakligi: number,
  ): IGatingSonuc {
    const alasim = this.malzemeDB.alasimAl(malzemeId);
    if (!alasim) {
      return this.bosHata();
    }

    const bbox = this._tmpBox.setFromObject(mesh);
    const boyut = this._tmpVec.set(0, 0, 0);
    bbox.getSize(boyut);

    const bboxHacimMm3 = boyut.x * boyut.y * boyut.z;
    const dolulukOrani = this.tahminiDolulukOrani(mesh);
    const hacimMm3 = bboxHacimMm3 * dolulukOrani;
    const hacimCm3 = hacimMm3 / 1000;

    const yuzeyAlaniMm2 = this.tahminiYuzeyAlani(boyut, dolulukOrani);
    const yuzeyAlaniCm2 = yuzeyAlaniMm2 / 100;

    const modulus = yuzeyAlaniCm2 > 0 ? hacimCm3 / yuzeyAlaniCm2 : 0;

    const yogunlukKgM3 = alasim.yogunlukSivi;
    const agirlikKg = (hacimCm3 / 1e6) * yogunlukKgM3;

    const hotSpotlar = this.hotSpotTespiti(bbox, boyut, modulus, hacimCm3);
    const besleyiciler = this.besleyiciBoyutlandir(hotSpotlar, modulus, alasim);

    const maxHiz = this.maxGateHizi(alasim.kategori, dokumTuru);
    const dolumSuresi = this.dolumSuresiHesapla(agirlikKg, dokumTuru);

    const debiCm3s = dolumSuresi > 0 ? hacimCm3 / dolumSuresi : hacimCm3;
    const debiM3s = debiCm3s * 1e-6;
    const gateToplamAlaniMm2 = maxHiz > 0 ? (debiM3s / maxHiz) * 1e6 : 100;
    const gateSayisi = Math.max(1, hotSpotlar.length);
    const tekGateAlaniMm2 = gateToplamAlaniMm2 / gateSayisi;

    const gateGenislik = Math.sqrt(tekGateAlaniMm2 * 3);
    const gateKalinlik = tekGateAlaniMm2 / gateGenislik;

    const gatelar = this.gateOlustur(hotSpotlar, bbox, gateGenislik, gateKalinlik, maxHiz);

    const sprueAltCap = Math.sqrt((4 * gateToplamAlaniMm2) / Math.PI);
    const sprueUstCap = sprueAltCap * SPRUE_KONIKLIK;
    const sprueYukseklik = boyut.y + 50;

    const merkez = new THREE.Vector3();
    bbox.getCenter(merkez);

    const sprue: ISprue = {
      pozisyon: new THREE.Vector3(merkez.x, bbox.max.y + sprueYukseklik / 2, merkez.z),
      ustCap: sprueUstCap,
      altCap: sprueAltCap,
      yukseklik: sprueYukseklik,
    };

    const runnerlar = this.runnerOlustur(sprue, gatelar, tekGateAlaniMm2);

    const besleyiciHacimCm3 = besleyiciler.reduce((t, b) => {
      const r = b.cap / 20;
      return t + Math.PI * r * r * (b.yukseklik / 10);
    }, 0);
    const sprueHacimCm3 = (Math.PI / 3) * sprueYukseklik / 10 *
      ((sprueUstCap / 20) ** 2 + (sprueAltCap / 20) ** 2 + (sprueUstCap / 20) * (sprueAltCap / 20));
    const runnerHacimCm3 = runnerlar.reduce((t, r) => {
      const uzunluk = new THREE.Vector3().subVectors(r.bitis, r.baslangic).length() / 10;
      return t + (r.kesitAlani / 100) * uzunluk;
    }, 0);
    const gateHacimCm3 = gatelar.reduce((t, g) => t + (g.genislik * g.kalinlik * 5) / 1000, 0);

    const yollukToplamCm3 = besleyiciHacimCm3 + sprueHacimCm3 + runnerHacimCm3 + gateHacimCm3;
    const brutHacimCm3 = hacimCm3 + yollukToplamCm3;
    const verimYuzdesi = brutHacimCm3 > 0 ? (hacimCm3 / brutHacimCm3) * 100 : 0;
    const brutAgirlikKg = (brutHacimCm3 / 1e6) * yogunlukKgM3;

    const puan = this.puanHesapla(verimYuzdesi, dolumSuresi, besleyiciler, modulus, alasim);

    return {
      besleyiciler,
      sprue,
      runnerlar,
      gatelar,
      verimYuzdesi: Math.round(verimYuzdesi * 10) / 10,
      tahminiDolumSuresi: Math.round(dolumSuresi * 100) / 100,
      puan: Math.round(puan),
      parcaHacimCm3: Math.round(hacimCm3 * 100) / 100,
      parcaYuzeyAlaniCm2: Math.round(yuzeyAlaniCm2 * 100) / 100,
      parcaModulus: Math.round(modulus * 1000) / 1000,
      parcaAgirlikKg: Math.round(agirlikKg * 1000) / 1000,
      brutAgirlikKg: Math.round(brutAgirlikKg * 1000) / 1000,
    };
  }

  meshOlustur(sonuc: IGatingSonuc): THREE.Group {
    const grup = new THREE.Group();
    grup.name = 'GatingSystem';

    const malzeme = new THREE.MeshPhongMaterial({
      color: 0xff8800,
      transparent: true,
      opacity: 0.45,
      side: THREE.DoubleSide,
      depthWrite: false,
    });

    const wireframeMalzeme = new THREE.LineBasicMaterial({
      color: 0xff6600,
      transparent: true,
      opacity: 0.7,
    });

    for (const b of sonuc.besleyiciler) {
      const geo = new THREE.CylinderGeometry(b.cap / 2, b.cap / 2, b.yukseklik, 24);
      const mesh = new THREE.Mesh(geo, malzeme);
      mesh.position.copy(b.pozisyon);
      mesh.name = `Besleyici_${b.hotSpotId}`;

      const edges = new THREE.EdgesGeometry(geo);
      const wire = new THREE.LineSegments(edges, wireframeMalzeme);
      mesh.add(wire);

      grup.add(mesh);
    }

    {
      const s = sonuc.sprue;
      const geo = new THREE.CylinderGeometry(s.ustCap / 2, s.altCap / 2, s.yukseklik, 24);
      const mesh = new THREE.Mesh(geo, malzeme);
      mesh.position.copy(s.pozisyon);
      mesh.name = 'Sprue';

      const edges = new THREE.EdgesGeometry(geo);
      const wire = new THREE.LineSegments(edges, wireframeMalzeme);
      mesh.add(wire);

      grup.add(mesh);
    }

    for (let i = 0; i < sonuc.runnerlar.length; i++) {
      const r = sonuc.runnerlar[i];
      const yonVec = new THREE.Vector3().subVectors(r.bitis, r.baslangic);
      const uzunluk = yonVec.length();
      if (uzunluk < 0.01) continue;

      const kenar = Math.sqrt(r.kesitAlani);
      const geo = new THREE.BoxGeometry(kenar, kenar, uzunluk);
      const mesh = new THREE.Mesh(geo, malzeme);

      const orta = new THREE.Vector3().addVectors(r.baslangic, r.bitis).multiplyScalar(0.5);
      mesh.position.copy(orta);
      mesh.lookAt(r.bitis);
      mesh.name = `Runner_${i}`;

      const edges = new THREE.EdgesGeometry(geo);
      const wire = new THREE.LineSegments(edges, wireframeMalzeme);
      mesh.add(wire);

      grup.add(mesh);
    }

    for (let i = 0; i < sonuc.gatelar.length; i++) {
      const g = sonuc.gatelar[i];
      const derinlik = 5;
      const geo = new THREE.BoxGeometry(g.genislik, g.kalinlik, derinlik);
      const mesh = new THREE.Mesh(geo, malzeme);
      mesh.position.copy(g.pozisyon);
      mesh.name = `Gate_${i}`;

      const edges = new THREE.EdgesGeometry(geo);
      const wire = new THREE.LineSegments(edges, wireframeMalzeme);
      mesh.add(wire);

      grup.add(mesh);
    }

    return grup;
  }

  // ============================================================================
  // GEOMETRI ANALIZ
  // ============================================================================

  private tahminiDolulukOrani(mesh: THREE.Mesh): number {
    const geo = mesh.geometry;
    if (!geo) return 0.6;

    if (geo.index) {
      const pos = geo.attributes['position'];
      if (!pos) return 0.6;

      const bbox = new THREE.Box3().setFromBufferAttribute(pos as THREE.BufferAttribute);
      const bboxBoyut = new THREE.Vector3();
      bbox.getSize(bboxBoyut);
      const bboxVol = bboxBoyut.x * bboxBoyut.y * bboxBoyut.z;
      if (bboxVol <= 0) return 0.6;

      let meshVol = 0;
      const idx = geo.index;
      const posArr = pos;
      const vA = new THREE.Vector3(), vB = new THREE.Vector3(), vC = new THREE.Vector3();

      for (let i = 0; i < idx.count; i += 3) {
        vA.fromBufferAttribute(posArr as THREE.BufferAttribute, idx.getX(i));
        vB.fromBufferAttribute(posArr as THREE.BufferAttribute, idx.getX(i + 1));
        vC.fromBufferAttribute(posArr as THREE.BufferAttribute, idx.getX(i + 2));
        meshVol += this.signedTetraVolume(vA, vB, vC);
      }

      const oran = Math.abs(meshVol) / bboxVol;
      return Math.max(0.1, Math.min(0.95, oran));
    }

    return 0.6;
  }

  private signedTetraVolume(a: THREE.Vector3, b: THREE.Vector3, c: THREE.Vector3): number {
    return (
      a.x * (b.y * c.z - b.z * c.y) +
      a.y * (b.z * c.x - b.x * c.z) +
      a.z * (b.x * c.y - b.y * c.x)
    ) / 6.0;
  }

  private tahminiYuzeyAlani(boyut: THREE.Vector3, doluluk: number): number {
    const bboxYuzey = 2 * (boyut.x * boyut.y + boyut.y * boyut.z + boyut.x * boyut.z);
    return bboxYuzey * (0.6 + doluluk * 0.4);
  }

  // ============================================================================
  // HOT SPOT TESPITI
  // ============================================================================

  private hotSpotTespiti(
    bbox: THREE.Box3,
    boyut: THREE.Vector3,
    genelModulus: number,
    hacimCm3: number,
  ): IHotSpot[] {
    const merkez = new THREE.Vector3();
    bbox.getCenter(merkez);

    const hotSpotlar: IHotSpot[] = [];

    hotSpotlar.push({
      id: 'merkez',
      pozisyon: merkez.clone(),
      modulus: genelModulus,
      hacimCm3: hacimCm3 * 0.4,
    });

    const enBuyukEksen = Math.max(boyut.x, boyut.y, boyut.z);
    const enKucukEksen = Math.min(boyut.x, boyut.y, boyut.z);
    const oranFarki = enBuyukEksen / Math.max(1, enKucukEksen);

    if (oranFarki > 2.5 && hacimCm3 > 50) {
      if (boyut.x === enBuyukEksen) {
        hotSpotlar.push({
          id: 'sol',
          pozisyon: new THREE.Vector3(bbox.min.x + boyut.x * 0.2, merkez.y, merkez.z),
          modulus: genelModulus * 0.85,
          hacimCm3: hacimCm3 * 0.3,
        });
        hotSpotlar.push({
          id: 'sag',
          pozisyon: new THREE.Vector3(bbox.max.x - boyut.x * 0.2, merkez.y, merkez.z),
          modulus: genelModulus * 0.85,
          hacimCm3: hacimCm3 * 0.3,
        });
      } else if (boyut.z === enBuyukEksen) {
        hotSpotlar.push({
          id: 'on',
          pozisyon: new THREE.Vector3(merkez.x, merkez.y, bbox.min.z + boyut.z * 0.2),
          modulus: genelModulus * 0.85,
          hacimCm3: hacimCm3 * 0.3,
        });
        hotSpotlar.push({
          id: 'arka',
          pozisyon: new THREE.Vector3(merkez.x, merkez.y, bbox.max.z - boyut.z * 0.2),
          modulus: genelModulus * 0.85,
          hacimCm3: hacimCm3 * 0.3,
        });
      }
    }

    return hotSpotlar;
  }

  // ============================================================================
  // BESLEYICI BOYUTLANDIRMA (Modulus Method)
  // ============================================================================

  private besleyiciBoyutlandir(
    hotSpotlar: IHotSpot[],
    parcaModulus: number,
    alasim: IDokumAlasim,
  ): IBesleyici[] {
    const riserModulus = parcaModulus * BESLEYICI_MODULUS_KATSAYI;
    const cekmeKatsayi = 1 + alasim.cekmeOrani * 5;

    return hotSpotlar.map(hs => {
      const efektifModulus = Math.max(riserModulus, hs.modulus * BESLEYICI_MODULUS_KATSAYI);
      const cap = efektifModulus * BESLEYICI_CAP_KATSAYI * 10 * cekmeKatsayi;
      const yukseklik = cap * BESLEYICI_YUKSEKLIK_ORANI;

      return {
        pozisyon: new THREE.Vector3(
          hs.pozisyon.x,
          hs.pozisyon.y + yukseklik / 2,
          hs.pozisyon.z,
        ),
        cap: Math.max(15, Math.round(cap * 10) / 10),
        yukseklik: Math.max(20, Math.round(yukseklik * 10) / 10),
        modulus: Math.round(efektifModulus * 1000) / 1000,
        hotSpotId: hs.id,
      };
    });
  }

  // ============================================================================
  // GATE VE RUNNER HESABI
  // ============================================================================

  private maxGateHizi(kategori: DokumAlasimKategori, dokumTuru: DokumTuru): number {
    let hiz = MAX_GATE_HIZ[kategori] ?? 1.0;
    if (dokumTuru === 'basincli') hiz *= 4;
    if (dokumTuru === 'savurma') hiz *= 1.5;
    return hiz;
  }

  private dolumSuresiHesapla(agirlikKg: number, dokumTuru: DokumTuru): number {
    const katsayi = dokumTuru === 'basincli' ? 0.4 : 1.2;
    return Math.max(0.5, katsayi * Math.sqrt(Math.max(0.01, agirlikKg)));
  }

  private gateOlustur(
    hotSpotlar: IHotSpot[],
    bbox: THREE.Box3,
    genislik: number,
    kalinlik: number,
    hiz: number,
  ): IGate[] {
    return hotSpotlar.map(hs => ({
      pozisyon: new THREE.Vector3(
        hs.pozisyon.x,
        bbox.min.y,
        hs.pozisyon.z,
      ),
      genislik: Math.max(3, Math.round(genislik * 10) / 10),
      kalinlik: Math.max(1.5, Math.round(kalinlik * 10) / 10),
      hiz: Math.round(hiz * 1000) / 1000,
    }));
  }

  private runnerOlustur(sprue: ISprue, gatelar: IGate[], gateAlaniMm2: number): IRunner[] {
    const sprueAlt = new THREE.Vector3(
      sprue.pozisyon.x,
      sprue.pozisyon.y - sprue.yukseklik / 2,
      sprue.pozisyon.z,
    );

    const runnerKesit = gateAlaniMm2 * 1.3;

    return gatelar.map(g => ({
      baslangic: sprueAlt.clone(),
      bitis: g.pozisyon.clone(),
      kesitAlani: Math.max(25, Math.round(runnerKesit * 10) / 10),
    }));
  }

  // ============================================================================
  // PUANLAMA
  // ============================================================================

  private puanHesapla(
    verim: number,
    dolumSuresi: number,
    besleyiciler: IBesleyici[],
    parcaModulus: number,
    alasim: IDokumAlasim,
  ): number {
    let puan = 50;

    if (verim > 70) puan += 15;
    else if (verim > 55) puan += 10;
    else if (verim > 40) puan += 5;
    else puan -= 10;

    const modulusYeterli = besleyiciler.every(
      b => b.modulus >= parcaModulus * 1.2,
    );
    if (modulusYeterli) puan += 20;
    else puan += 5;

    if (dolumSuresi > 0.3 && dolumSuresi < 30) puan += 10;

    if (alasim.akiskanlikIndeksi > 80) puan += 5;
    else if (alasim.akiskanlikIndeksi < 50) puan -= 5;

    if (alasim.sicakYirtilmaEgilimi < 30) puan += 5;
    else if (alasim.sicakYirtilmaEgilimi > 50) puan -= 5;

    return Math.max(0, Math.min(100, puan));
  }

  // ============================================================================
  // YARDIMCI
  // ============================================================================

  private bosHata(): IGatingSonuc {
    return {
      besleyiciler: [],
      sprue: { pozisyon: new THREE.Vector3(), ustCap: 0, altCap: 0, yukseklik: 0 },
      runnerlar: [],
      gatelar: [],
      verimYuzdesi: 0,
      tahminiDolumSuresi: 0,
      puan: 0,
      parcaHacimCm3: 0,
      parcaYuzeyAlaniCm2: 0,
      parcaModulus: 0,
      parcaAgirlikKg: 0,
      brutAgirlikKg: 0,
    };
  }
}
